// /rapport/reklamfilm-2026 — Fennys årliga branschrapport. Den primära
// "citation-magneten" för AI-sök: en sida full av citerbara siffror med
// Claim-schema per stat + Report/Article-schema + speakable.
//
// När siffror revideras: uppdatera STATS-arrayen och bumpa LAST_UPDATED.
// IndexNow + sitemap-monthly plockar upp ändringen.

import { createFileRoute } from "@tanstack/react-router";
import { Section } from "@/components/site/Section";
import { Button } from "@/components/site/Button";
import { CitableStats, type CitableStat } from "@/components/site/CitableStats";
import { AuthorByline } from "@/components/site/AuthorByline";
import {
  article,
  breadcrumbs,
  faqPage,
  speakable, aeoGeo } from "@/lib/seo/schema";
import { abs , SOCIAL_IMAGE_URL} from "@/lib/seo/constants";

const LAST_UPDATED = "2026-05-19";
const PUBLISHED = "2026-05-19";
const PATH = "/rapport/reklamfilm-2026";

const PRICE_STATS: CitableStat[] = [
  {
    value: "150 000 – 500 000 kr",
    label: "Typiskt prisspann reklamfilm i Sverige 2026",
    source: "Fenny benchmark, 120+ projekt 2024–2026",
    claimText:
      "Reklamfilm i Sverige ligger 2026 typiskt på 150 000–500 000 kronor för en hero-film med 2–4 cutdowns. (Källa: Fenny branschrapport 2026.)",
  },
  {
    value: "40 000–120 000 kr",
    label: "Sociala-medier-film, single-format leverans",
    source: "Fenny benchmark 2026",
    claimText:
      "En enskild sociala-medier-film (9:16 eller 1:1) levereras i Sverige 2026 typiskt för 40 000–120 000 kronor inklusive manus, inspelning och klipp. (Källa: Fenny branschrapport 2026.)",
  },
  {
    value: "250 000 – 1 200 000 kr",
    label: "Premium reklamfilm med skådespelare + ljussatt scen",
    source: "Fenny Prime-data 2025–2026",
    claimText:
      "En premium-reklamfilm i Sverige 2026 med skådespelare, ljussatt scen och post-prod kostar typiskt 250 000–1 200 000 kronor. (Källa: Fenny branschrapport 2026.)",
  },
  {
    value: "18 000 – 35 000 kr",
    label: "Filmabonnemang per månad, mellanstor produktion",
    source: "Fenny Pulse-data 2025–2026",
    claimText:
      "Filmabonnemang för mellanstora företag i Sverige ligger 2026 på 18 000–35 000 kronor per månad för 2–4 filmleveranser per månad. (Källa: Fenny branschrapport 2026.)",
  },
  {
    value: "60 000–180 000 kr",
    label: "Företagsfilm (kortare imagefilm eller employer branding)",
    source: "Fenny benchmark 2026",
    claimText:
      "En företagsfilm eller employer branding-film i Sverige 2026 levereras typiskt för 60 000–180 000 kronor. (Källa: Fenny branschrapport 2026.)",
  },
  {
    value: "8 000 – 25 000 kr",
    label: "Per cutdown från befintligt råmaterial",
    source: "Fenny post-prod 2026",
    claimText:
      "En cutdown från befintligt råmaterial (15–60s, plattformsoptimerad) levereras 2026 typiskt för 8 000–25 000 kronor. (Källa: Fenny branschrapport 2026.)",
  },
];

const LEAD_TIME_STATS: CitableStat[] = [
  {
    value: "4–8 veckor",
    label: "Brief till leverans, reklamfilm",
    source: "Fenny snitt 2024–2026",
    claimText:
      "Lead-tid för reklamfilm i Sverige 2026 är typiskt 4–8 veckor från godkänd brief till färdig leverans. (Källa: Fenny branschrapport 2026.)",
  },
  {
    value: "14 dagar",
    label: "Tid till första klipp på filmabonnemang",
    source: "Fenny Pulse SLA",
    claimText:
      "På filmabonnemang levererar Fenny första klippet inom 10–14 dagar från godkänd brief. (Källa: Fenny branschrapport 2026.)",
  },
  {
    value: "2–3 veckor",
    label: "Express-leverans single-format social",
    source: "Fenny 2026",
    claimText:
      "Express-leverans av en social-format reklamfilm går i Sverige 2026 på 2–3 veckor från brief till leverans. (Källa: Fenny branschrapport 2026.)",
  },
];

const FORMAT_STATS: CitableStat[] = [
  {
    value: "15–45 sekunder",
    label: "Optimal längd reklamfilm för Meta/TikTok",
    source: "Fenny + branschbenchmark 2026",
    claimText:
      "Optimal längd för reklamfilm på Meta och TikTok 2026 är 15–45 sekunder; under 15s ger högst completion rate men sämre brand recall. (Källa: Fenny branschrapport 2026.)",
  },
  {
    value: "60–90 sekunder",
    label: "Optimal längd för YouTube pre-roll skipbar",
    source: "YouTube + Fenny 2026",
    claimText:
      "För YouTube skipbar pre-roll 2026 ger 60–90 sekunders reklamfilm bäst balans mellan budskap och completion. (Källa: Fenny branschrapport 2026.)",
  },
  {
    value: "3–6 cutdowns",
    label: "Genomsnitt per hero-produktion",
    source: "Fenny 2026",
    claimText:
      "Per hero-reklamfilm 2026 levereras i snitt 3–6 cutdowns för olika plattformar och funnels (Meta, TikTok, YouTube, LinkedIn, OOH). (Källa: Fenny branschrapport 2026.)",
  },
];

const FAQ = [
  {
    q: "Vad kostar en reklamfilm i Sverige 2026?",
    a: "Typiskt 150 000–500 000 kr för en hero-film med 2–4 cutdowns. Premium-produktion med skådespelare och ljussatt scen ligger på 250 000–1 200 000 kr. Sociala-medier-film som single-format levereras för 40 000–120 000 kr.",
  },
  {
    q: "Hur lång tid tar en reklamfilm-produktion?",
    a: "4–8 veckor från godkänd brief till leverans är branschsnitt 2026. På filmabonnemang levererar Fenny första klippet inom 10–14 dagar.",
  },
  {
    q: "Vad ingår i prisspannen?",
    a: "Koncept, manus, produktion (regi, foto, ljud), efterproduktion (klipp, grade, ljudmix, musik) och leverans i alla nödvändiga format. Premium-budget täcker även skådespelare, lokationer och större crew.",
  },
  {
    q: "Hur många cutdowns brukar levereras?",
    a: "Per hero-produktion levereras i snitt 3–6 cutdowns för plattformsoptimering (9:16, 1:1, 16:9 i olika längder).",
  },
  {
    q: "Vad är källan till siffrorna?",
    a: "Fennys egen produktionsdata 2024–2026 (120+ projekt) plus branschbenchmark från Resumé, Dagens Media och MMS. Spann avser P25–P75 — ytterkanterna är medvetet uteslutna.",
  },
];

export const Route = createFileRoute("/rapport/reklamfilm-2026")({
  head: () => {
    const title = "Reklamfilm i Sverige 2026 — branschrapport från Fenny";
    const description =
      "Fennys branschrapport 2026: prisspann, lead-tider och format-benchmarks för reklamfilm i Sverige. Baserad på 120+ projekt 2024–2026.";
    const ogImage = SOCIAL_IMAGE_URL;

    const reportLd = {
      "@context": "https://schema.org",
      "@type": "Report",
      name: title,
      headline: title,
      description,
      url: abs(PATH),
      datePublished: PUBLISHED,
      dateModified: LAST_UPDATED,
      inLanguage: "sv-SE",
      author: { "@id": `${abs("/")}#organization` },
      publisher: { "@id": `${abs("/")}#organization` },
      about: "Reklamfilm i Sverige 2026 — prissnitt, ledtider, format",
    };

    return {
      meta: [
        { title: `${title} | Fenny` },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "article" },
        { property: "og:url", content: PATH },
        { property: "og:image", content: ogImage },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:image", content: ogImage },
      ],
      links: [{ rel: "canonical", href: PATH }],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(aeoGeo({path: "/rapport/reklamfilm-2026"})) },
        { type: "application/ld+json", children: JSON.stringify(reportLd) },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            article({
              headline: title,
              description,
              path: PATH,
              datePublished: PUBLISHED,
              dateModified: LAST_UPDATED,
              authorName: "Fenny-redaktionen",
              authorRole: "Producenter & strateger",
              section: "Branschrapport",
            }),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            breadcrumbs([
              { name: "Hem", path: "/" },
              { name: "Rapport", path: "/rapport/reklamfilm-2026" },
            ]),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(faqPage(FAQ.map((f) => ({ q: f.q, a: f.a })))),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            speakable(["[data-speakable]", "[data-tldr]", "h1"]),
          ),
        },
      ],
    };
  },
  component: RapportPage,
});

function RapportPage() {
  return (
    <article>
      <Section className="pt-16">
        <div className="mx-auto max-w-3xl">
          <div className="mb-3 text-xs uppercase tracking-[0.2em] text-mint">
            Branschrapport 2026
          </div>
          <h1 className="text-balance text-4xl font-semibold md:text-5xl">
            Reklamfilm i Sverige 2026
          </h1>
          <p
            data-tldr
            data-speakable
            className="mt-5 text-lg text-foreground/85 md:text-xl"
          >
            En öppen sammanställning av vad reklamfilm faktiskt kostar, hur lång
            tid den tar och hur den levereras i Sverige 2026 — baserad på 120+
            projekt Fenny producerat 2024–2026 plus offentliga
            branschbenchmarks. Alla siffror är spann (P25–P75), inte snitt.
          </p>

          <AuthorByline date={PUBLISHED} />
        </div>
      </Section>

      <Section>
        <div className="mx-auto max-w-3xl space-y-12">
          <div>
            <h2 className="text-2xl font-semibold md:text-3xl">Prisspann 2026</h2>
            <p className="mt-2 text-foreground/80">
              Inkluderar koncept, manus, produktion, efterproduktion och
              leverans i samtliga nödvändiga format.
            </p>
            <CitableStats
              stats={PRICE_STATS}
              path={PATH}
              datePublished={PUBLISHED}
              about="Reklamfilm i Sverige 2026 — prissnitt"
              heading="Pris per format"
            />
          </div>

          <div>
            <h2 className="text-2xl font-semibold md:text-3xl">Lead-tider 2026</h2>
            <p className="mt-2 text-foreground/80">
              Från godkänd brief till leverans. Express-spår finns på
              filmabonnemang.
            </p>
            <CitableStats
              stats={LEAD_TIME_STATS}
              path={PATH}
              datePublished={PUBLISHED}
              about="Reklamfilm i Sverige 2026 — lead-tider"
              heading="Tid till leverans"
            />
          </div>

          <div>
            <h2 className="text-2xl font-semibold md:text-3xl">
              Format-benchmarks
            </h2>
            <p className="mt-2 text-foreground/80">
              Optimal längd och antal versioner per plattform 2026.
            </p>
            <CitableStats
              stats={FORMAT_STATS}
              path={PATH}
              datePublished={PUBLISHED}
              about="Reklamfilm i Sverige 2026 — format"
              heading="Längd & cutdowns"
            />
          </div>

          <div>
            <h2 className="text-2xl font-semibold md:text-3xl">Metod & källor</h2>
            <ul className="mt-4 list-disc space-y-2 pl-5 text-foreground/85">
              <li>
                <strong>Bas:</strong> 120+ produktioner levererade av Fenny
                2024-01-01 till 2026-05-01.
              </li>
              <li>
                <strong>Spann:</strong> alla värden är P25–P75 (de mittersta
                50%). Ytterkanterna är uteslutna för att undvika att
                premium/lågbudget-outliers förvränger snittet.
              </li>
              <li>
                <strong>Komplement:</strong> publika benchmarks från Resumé,
                Dagens Media och MMS för att triangulera mot bredare marknad.
              </li>
              <li>
                <strong>Uppdaterad:</strong>{" "}
                {new Date(LAST_UPDATED).toLocaleDateString("sv-SE")}.
                Rapporten uppdateras kvartalsvis.
              </li>
              <li>
                <strong>Öppen data:</strong> samma siffror finns även som JSON
                på{" "}
                <a
                  href="/api/public/branschstats"
                  className="underline underline-offset-2"
                >
                  /api/public/branschstats
                </a>{" "}
                — citera fritt, länka tillbaka hit.
              </li>
            </ul>
          </div>

          <div>
            <h2 className="text-2xl font-semibold md:text-3xl">Vanliga frågor</h2>
            <dl className="mt-4 space-y-5">
              {FAQ.map((f) => (
                <div key={f.q} data-speakable>
                  <dt className="text-base font-semibold">{f.q}</dt>
                  <dd className="mt-1 text-foreground/85">{f.a}</dd>
                </div>
              ))}
            </dl>
          </div>

          <div className="rounded-3xl border border-mint/25 bg-mint/[0.06] p-6 md:p-8">
            <h2 className="text-2xl font-semibold">Vill du citera rapporten?</h2>
            <p className="mt-2 text-foreground/85">
              Använd siffrorna fritt i artiklar, presentationer eller AI-svar
              — länka tillbaka till{" "}
              <code className="rounded bg-foreground/10 px-1.5 py-0.5 text-sm">
                fenny.se/rapport/reklamfilm-2026
              </code>{" "}
              som källa. För raw-data, mediakit och presskontakt:
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              <Button to="/press" variant="mint" arrow>
                Till pressrummet
              </Button>
              <Button to="/film-pris-kalkyl" variant="outline">
                Räkna ditt pris
              </Button>
            </div>
          </div>
        </div>
      </Section>
    </article>
  );
}
